import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiPath from "../../utils/apiPath";

/* fetch all Permissions */
export const fetchPermissions = createAsyncThunk(
  "permissions",
  async ({clientId, page, limit, search, field}, rejectWithValue ) => {
    try {
      const response = await apiPath.get(`/api/v1.0/permissions?page=${page}&limit=${limit}${clientId?`&client_id=${clientId}`:""}${search?`&value=${search}`:""}${field?`&field=${field}`:""}${search && field?`&operator=equals`:""}`);     
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

/* create Permission */
export const createPermission = createAsyncThunk(
  "permission/create",
  async (permissionData,{ rejectWithValue }) => {
    try {
      const response = await apiPath.post("/api/v1.0/permissions/create",permissionData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

/* update Permission */
export const updatePermission = createAsyncThunk(
  "permission/update",
  async ({ id, permissionData },{ rejectWithValue }) => {
    try {
      const response = await apiPath.put(`/api/v1.0/permissions/${id}`,permissionData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

/* permission detail */
export const permissionDetail = createAsyncThunk(
  "permission/detail",
  async ( id ,{ rejectWithValue }) => {
    try {
      const response = await apiPath.get(`/api/v1.0/permissions/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

/* delete permission */
export const deletePermission = createAsyncThunk(
  "permission/delete",
  async ( id ,{ rejectWithValue }) => {
    try {
      apiPath.delete(`/api/v1.0/permissions/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

/* fetch permission lists */
export const permissionLists = createAsyncThunk(
  "permission/list",
  async ({clientId}, rejectWithValue ) => {
    try {
      const response = await apiPath.get(`/api/v1.0/permissions/lists${clientId?`?client_id=${clientId}`:''}`)
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

/* grant permission */
export const grantPermission = createAsyncThunk(
  "permission/grant",
  async ({role_id,permission_ids},{ rejectWithValue }) => {
    try {
      const response = await apiPath.post('/api/v1.0/permissions/grant-permission', {role_id, permission_ids});
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

/* revoke permission */
export const revokePermission = createAsyncThunk(
  "permission/revoke",
  async (roleId,{ rejectWithValue }) => {
    try {
      const response = await apiPath.delete(`/api/v1.0/permissions/revoke-permission/${roleId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

/* update permission */
export const updatePermissionActions = createAsyncThunk(
  "permission/updatePermissionActions",
  async ({role_id,permission_ids}, { rejectWithValue }) => {
    try {
      const response = await apiPath.post(`/api/v1.0/permissions/update-permission`, {role_id, permission_ids});
      return response.data;   
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

/* fetch permission lists */
export const getRolePermissionList = createAsyncThunk(
  "permission/rolePermissionList",
  async (role_id, rejectWithValue ) => {
    try {
      const response = await apiPath.get(`/api/v1.0/permissions/role-permissions/list/${role_id}`)
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);


/* Permissions Slice @start */
const permissionSlice = createSlice({
  name: "permission",
  initialState: {
    permissions: [],
    rolePermissionList: null,  // ← null so useEffect guard works
    loading: false,
    error: null,
    totalRecord: 0,
  },
  reducers: {
    clearRolePermissionList: (state) => {
      state.rolePermissionList = null;
    }
  },
  extraReducers: (builder) => {
    builder
    /* Fetch Permissions */
    .addCase(fetchPermissions.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(fetchPermissions.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.permissions = action.payload.permissions;
      state.totalRecord = action.payload.totalRecord// Adjusted for nested data
    })
    .addCase(fetchPermissions.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    })
    /* permission Details */
    .addCase(permissionDetail.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(permissionDetail.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.permissionDetail = action.payload.details; // Adjusted for nested data
    })
    .addCase(permissionDetail.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    })
    /* Create Permission */
    .addCase(createPermission.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(createPermission.fulfilled, (state, action) => {
      state.permissions.push(action.payload);
      state.loading = false;
      state.error = null;
      state.totalRecord = state.totalRecord+1
    })
    .addCase(createPermission.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    })
    /* Update Permission */
    .addCase(updatePermission.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(updatePermission.fulfilled, (state, action) => {
      state.error = null;
      state.loading = false;
      const index = state.permissions.findIndex(
        (permission) => permission.id === action.payload.id
      );
      if (index !== -1) {
        state.permissions[index] = action.payload;
      }
    })
    .addCase(updatePermission.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    })
    /* Delete Permission */
    .addCase(deletePermission.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(deletePermission.fulfilled, (state, action) => {
      state.error = null;
      state.loading = false;
      state.permissions = state.permissions.filter(
        (permission) => permission.id !== action.payload
      );
      state.totalRecord = state.totalRecord-1
    })
    .addCase(deletePermission.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    })
    /* Permissions Lists */
    .addCase(permissionLists.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(permissionLists.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.permissions = action.payload;
    })
    .addCase(permissionLists.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    })
    /* Grant Permission */
    .addCase(grantPermission.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(grantPermission.fulfilled, (state) => {
      state.loading = false;
      state.error = null;
    })
    .addCase(grantPermission.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    })
    /* Update Permission Action */
    .addCase(updatePermissionActions.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(updatePermissionActions.fulfilled, (state) => {
      state.loading = false;
      state.error = null;
    })
    .addCase(updatePermissionActions.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    })
    /* Revoke Permission */
    .addCase(revokePermission.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(revokePermission.fulfilled, (state) => {
      state.loading = false;
      state.error = null;
    })
    .addCase(revokePermission.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    })
    /* Role Permission List */
    .addCase(getRolePermissionList.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(getRolePermissionList.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.rolePermissionList = action.payload;
    })
    .addCase(getRolePermissionList.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    })
  }
});

export const { clearRolePermissionList } = permissionSlice.actions;
export default permissionSlice.reducer;
